import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Panel, borderStyle, inputCls } from "../ui";
import MediaPicker from "../MediaPicker";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

export default function BuildsAdmin() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [picker, setPicker] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/admin/builds").then((r) => setRows(r.data.builds)).catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    try {
      const body = {
        title: edit.title, description: edit.description, vehicle: edit.vehicle,
        image: edit.image, published: edit.published, order: Number(edit.order || 0),
      };
      if (edit.id) await adminApi.put(`/admin/builds/${edit.id}`, body);
      else await adminApi.post("/admin/builds", body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    }
  }

  async function remove(id) {
    if (!window.confirm("Remove this build photo?")) return;
    await adminApi.delete(`/admin/builds/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div data-testid="admin-builds">
      <AdminHeader title="Our builds" subtitle={rows ? `${rows.length} gallery photos` : ""}>
        <button type="button" className="btn-gold" data-testid="admin-new-build-button"
          onClick={() => setEdit({ title: "", description: "", vehicle: "", image: "", order: 0, published: true })}>
          <Plus className="h-4 w-4" /> Add build
        </button>
      </AdminHeader>

      {edit && (
        <Panel className="mb-5">
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <SmartImage src={edit.image} alt="" ratio="aspect-square" fit="cover" fallbackLabel={edit.title} />
              <button type="button" onClick={() => setPicker(true)} className="btn-outline-nq mt-2 w-full px-3 py-1.5 text-xs">Choose image</button>
            </div>
            <div className="grid gap-3 sm:col-span-3">
              <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Title" className={inputCls} style={borderStyle} data-testid="build-title-input" />
              <input value={edit.vehicle || ""} onChange={(e) => setEdit({ ...edit, vehicle: e.target.value })} placeholder="Vehicle e.g. 79 Series" className={inputCls} style={borderStyle} />
              <textarea rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Description" className={inputCls} style={borderStyle} />
              <div className="flex gap-3">
                <button type="button" onClick={save} className="btn-gold" data-testid="build-save-button"><Save className="h-4 w-4" /> Save</button>
                <button type="button" onClick={() => setEdit(null)} className="btn-outline-nq">Cancel</button>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {!rows ? <PageLoader /> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {rows.map((b) => (
            <div key={b.id} className="card-nq" data-testid="admin-build-item">
              <SmartImage src={b.image} alt={b.title} ratio="aspect-square" fit="cover" />
              <div className="p-2">
                <p className="truncate text-xs font-medium">{b.title}</p>
                <div className="mt-1.5 flex gap-2">
                  <button type="button" onClick={() => setEdit(b)} className="text-[10px] gold-text hover:underline" data-testid="admin-build-edit">Edit</button>
                  <button type="button" onClick={() => remove(b.id)} className="flex items-center gap-1 text-[10px] text-[color:var(--nq-stone)] hover:text-[#e07a72]">
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {picker && <MediaPicker onClose={() => setPicker(false)} onPick={(m) => { setEdit((v) => ({ ...v, image: m.url })); setPicker(false); }} />}
    </div>
  );
}
